// The bell in the global bar and the inbox list it opens (wave 13, w13-notify). main.ts mounts bell() beside the
// account menu for signed-in players; /me/inbox (pages/me-inbox.ts) shows the same list in full. Styles are
// injected once under .bell / .inbox so style.css stays the shell's.

import { gameById, getCatalogue, type Catalogue } from "./catalogue.ts";
import { clear, h, link } from "./dom.ts";
import { describe, loadInbox, markRead, prefsOf, userIds, type Inbox, type InboxItem, type Names, type Said } from "./notify.ts";
import { ago } from "./pages/me-home.ts";
import { userName } from "./pages/game.ts";
import { getFullMe } from "./pages/me-kit.ts";

const SVG = "http://www.w3.org/2000/svg";

/** Lucide's line icons (the launcher's set), from fixed path data. */
const PATHS: Record<Said["icon"] | "bell", string[]> = {
  bell: ["M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9", "M10.3 21a1.94 1.94 0 0 0 3.4 0"],
  calendar: ["M8 2v4", "M16 2v4", "M3 10h18", "M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"],
  friend: ["M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2", "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "M19 8v6", "M22 11h-6"],
  message: ["M7.9 20A9 9 0 1 0 4 16.1L2 22Z"],
  note: ["M12 20h9", "M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"],
  game: ["M6 12h4", "M8 10v4", "M15 13h.01", "M18 11h.01", "M17.32 5H6.68a4 4 0 0 0-3.98 3.59l-.9 7.4A3 3 0 0 0 4.79 19.4c1.09 0 2.07-.6 2.6-1.55L8.6 15.7a2 2 0 0 1 1.74-1.02h3.32a2 2 0 0 1 1.74 1.02l1.2 2.15A3 3 0 0 0 19.21 19.4a3 3 0 0 0 2.99-3.4l-.9-7.4A4 4 0 0 0 17.32 5Z"],
  reply: ["M9 17 4 12l5-5", "M20 18v-2a4 4 0 0 0-4-4H4"],
  fork: ["M6 3v12", "M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z", "M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z", "M18 9a9 9 0 0 1-9 9"],
};

export function icon(name: keyof typeof PATHS, size = 18): SVGElement {
  const s = document.createElementNS(SVG, "svg");
  for (const [k, v] of Object.entries({ viewBox: "0 0 24 24", width: String(size), height: String(size), fill: "none", stroke: "currentColor", "stroke-width": "2", "stroke-linecap": "round", "stroke-linejoin": "round", "aria-hidden": "true" })) s.setAttribute(k, v);
  for (const d of PATHS[name]) {
    const p = document.createElementNS(SVG, "path");
    p.setAttribute("d", d);
    s.append(p);
  }
  return s;
}

let styled = false;
export function bellStyles(): void {
  if (styled || typeof document === "undefined") return;
  styled = true;
  document.head.append(
    h(
      "style",
      { id: "bell-styles" },
      `
.bell { position: relative; }
.bell > summary { list-style: none; cursor: pointer; display: grid; place-items: center; width: 38px; height: 38px; border-radius: 10px; color: #cfcfd6; position: relative; }
.bell > summary::-webkit-details-marker { display: none; }
.bell > summary:hover, .bell[open] > summary { background: var(--panel); color: #fff; }
.bell > summary:focus-visible { outline: 2px solid var(--ruby); outline-offset: 2px; }
.bell-count { position: absolute; top: 3px; right: 2px; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; background: var(--ruby); color: #121216; font-size: .6875rem; font-weight: 700; line-height: 18px; text-align: center; box-shadow: 0 0 0 2px var(--bg); font-variant-numeric: tabular-nums; }
.bell-panel { position: absolute; right: -8px; top: calc(100% + 8px); width: min(400px, calc(100vw - 24px)); background: var(--panel); border: 1px solid var(--border); border-radius: 14px; box-shadow: 0 16px 48px rgba(0,0,0,.55); z-index: 40; display: grid; overflow: hidden; }
.bell-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; padding: 14px 16px 10px; }
.bell-head h2 { margin: 0; font-size: 1rem; font-weight: 700; }
.inbox-link { background: none; border: 0; padding: 0; color: var(--dim); font: inherit; font-size: .8125rem; cursor: pointer; text-decoration: none; }
.inbox-link:hover { color: var(--text); text-decoration: underline; }
.bell-panel .inbox-list { max-height: min(60vh, 460px); overflow-y: auto; padding: 0 6px 6px; }
.bell-foot { display: flex; justify-content: space-between; gap: 12px; padding: 10px 16px; border-top: 1px solid var(--border); }
.inbox-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 2px; }
.visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.inbox-row { position: relative; display: grid; grid-template-columns: 36px 1fr auto; gap: 12px; align-items: start; padding: 10px; border-radius: 10px; color: var(--text); text-decoration: none; }
a.inbox-row:hover, a.inbox-row:focus-visible { background: var(--hover); outline: none; }
.inbox-ic { width: 36px; height: 36px; border-radius: 10px; background: var(--card); display: grid; place-items: center; color: var(--dim); }
.inbox-row.unread .inbox-ic { color: var(--ruby); background: rgba(255,90,122,.12); }
.inbox-line { font-size: .9rem; line-height: 1.35; overflow-wrap: anywhere; }
.inbox-row.unread .inbox-line { font-weight: 600; }
.inbox-detail { color: var(--dim); font-size: .8125rem; line-height: 1.4; margin-top: 2px; overflow-wrap: anywhere; }
.inbox-when { color: var(--dim); font-size: .75rem; white-space: nowrap; padding-top: 2px; display: flex; align-items: center; gap: 6px; }
.inbox-row.unread .inbox-when::after { content: ""; width: 8px; height: 8px; border-radius: 50%; background: var(--ruby); }
.inbox-empty { padding: 18px 16px 22px; color: var(--dim); font-size: .875rem; line-height: 1.5; }
.inbox-empty strong { display: block; color: var(--text); font-size: .95rem; margin-bottom: 4px; }
.inbox-missing { margin: 0 16px 8px; color: var(--faint); font-size: .75rem; }
@media (max-width: 560px) {
  .bell-panel { position: fixed; left: 12px; right: 12px; top: 64px; width: auto; }
}
`,
    ),
  );
}

/** Game titles from the catalogue; player names from accounts (looked up in one batch). */
export async function namesFor(items: InboxItem[], cat: Catalogue | null): Promise<Names> {
  const ids = userIds(items);
  const got = new Map(await Promise.all(ids.map(async (id) => [id, await userName(id).catch(() => "A player")] as const)));
  return {
    game: (id) => (id && cat ? gameById(cat, id)?.title ?? null : null),
    user: (id) => (id ? got.get(id) ?? "A player" : "A player"),
  };
}

/** The rows. Clicking one marks it read and follows its link. */
export function inboxList(items: InboxItem[], names: Names, onOpen?: (i: InboxItem) => void): HTMLElement {
  const rows: HTMLElement[] = [];
  for (const i of items) {
    const s = describe(i, names);
    if (!s) continue;
    const body = [
      i.read ? null : h("span", { class: "visually-hidden" }, "Unread: "),
      h("span", { class: "inbox-ic" }, icon(s.icon)),
      h("span", null, h("div", { class: "inbox-line" }, s.line), s.detail ? h("div", { class: "inbox-detail" }, s.detail) : null),
      h("span", { class: "inbox-when" }, ago(i.at)),
    ];
    const cls = `inbox-row${i.read ? "" : " unread"}`;
    const row = s.href
      ? link(s.href, { class: cls, onclick: () => {
          onOpen?.(i);
          if (!i.read) void markRead([i]);
          i.read = true;
        } }, ...body)
      : h("div", { class: cls }, ...body);
    rows.push(h("li", null, row));
  }
  return h("ul", { class: "inbox-list" }, rows);
}

export function emptyInbox(): HTMLElement {
  return h(
    "div",
    { class: "inbox-empty" },
    h("strong", null, "Nothing yet"),
    "Event reminders, friends, replies and the notes you left that ship all land here.",
  );
}

const POLL_MS = 90_000;
const REFRESH = "banger:inbox-changed";

/** Pages that read or change the inbox (/me/inbox's Mark all read) tell the bell to look again now. */
export const inboxChanged = () => window.dispatchEvent(new Event(REFRESH));

let cached: { user: string; el: HTMLElement } | null = null;

/**
 * The bell: an unread count, and the latest items in a panel. The global bar redraws often (session, season), so one
 * bell per signed-in player is kept and moved into each new bar rather than rebuilt (and re-polled).
 */
export function bell(userId: string): HTMLElement {
  if (cached?.user === userId) return cached.el;
  const el = makeBell();
  cached = { user: userId, el };
  return el;
}

function makeBell(): HTMLElement {
  bellStyles();
  const count = h("span", { class: "bell-count", hidden: "" });
  const summary = h("summary", { "aria-label": "Notifications" }, icon("bell", 20), count);
  const list = h("div", null, h("div", { class: "inbox-empty" }, "Loading…"));
  const readAll = h("button", { class: "inbox-link", type: "button", hidden: "" }, "Mark all read");
  const panel = h(
    "div",
    { class: "bell-panel", role: "dialog", "aria-label": "Notifications" },
    h("div", { class: "bell-head" }, h("h2", null, "Notifications"), readAll),
    list,
    h("div", { class: "bell-foot" }, link("/me/inbox", { class: "inbox-link" }, "See all"), link("/me/settings#notifications", { class: "inbox-link" }, "Settings")),
  );
  const root = h("details", { class: "bell" }, summary, panel) as HTMLDetailsElement;

  let inbox: Inbox | null = null;
  const setCount = (n: number) => {
    count.textContent = n > 99 ? "99+" : String(n);
    count.toggleAttribute("hidden", n === 0);
    summary.setAttribute("aria-label", n ? `Notifications, ${n} unread` : "Notifications");
    readAll.toggleAttribute("hidden", n === 0);
  };
  const paint = async () => {
    if (!inbox) return;
    const shown = inbox.items.slice(0, 12);
    const names = await namesFor(shown, await getCatalogue().catch(() => null));
    const rows = inboxList(shown, names, () => {
      root.open = false;
      setCount(inbox!.items.filter((x) => !x.read).length);
    });
    clear(list, rows.childElementCount ? rows : emptyInbox(), inbox.missing.length ? h("p", { class: "inbox-missing" }, "Some notifications couldn't load. They'll be back shortly.") : null);
  };
  const refresh = async () => {
    if (cached?.el !== root) return stop();
    try {
      const me = await getFullMe();
      inbox = await loadInbox(prefsOf(me.notify));
      setCount(inbox.unread);
      if (root.open) await paint();
    } catch {
      /* signed out or offline: the bell stays quiet */
    }
  };
  readAll.addEventListener("click", async () => {
    if (!inbox) return;
    for (const i of inbox.items) i.read = true;
    setCount(0);
    await paint();
    await markRead("all");
  });
  root.addEventListener("toggle", () => {
    if (root.open) void paint();
  });
  // "See all" and Settings close the panel; Esc does too
  panel.addEventListener("click", (e) => {
    if ((e.target as Element).closest(".bell-foot a")) root.open = false;
  });
  root.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && root.open) {
      root.open = false;
      summary.focus();
    }
  });
  const timer = setInterval(() => {
    if (document.visibilityState === "visible") void refresh();
  }, POLL_MS);
  const onFocus = () => void refresh();
  window.addEventListener("focus", onFocus);
  window.addEventListener(REFRESH, onFocus);
  function stop() {
    clearInterval(timer);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener(REFRESH, onFocus);
  }
  queueMicrotask(() => void refresh()); // after bell() has cached this one
  return root;
}

/** Closes an open bell panel on a click elsewhere (main.ts wires it next to the account menu's). */
export function closeBellOutside(e: MouseEvent): void {
  const open = document.querySelector("details.bell[open]");
  if (open && !(e.target as Element).closest("details.bell")) open.removeAttribute("open");
}
